import { currencyExact, quantity } from './format';

// The date × service · SKU pivot model, shared by the superadmin Cost & Billing
// page (live provider rows) and the client Billing page (stored rows).
//
// Money and usage are pivoted differently on purpose, because different things
// split them. An amount belongs to a **currency**: cost and billing columns are
// split by currency and totalled per currency, since adding USD to SGD would be
// meaningless. A quantity belongs to a **unit** — GB months, OCPU hours,
// requests: usage columns are split by unit and carry no currency at all.
// Heading a column of GB months "(USD)" said nothing, and it also split one
// SKU's usage across two columns whenever the provider reported it in two
// currencies. Switching the metric therefore swaps the whole table, not just
// the numbers in it.

export const isMoneyMetric = (m) => m === 'cost' || m === 'billing';

// A row's SKU name is not always populated; fall back to the SKU id so the
// column stays identifiable rather than collapsing every unnamed SKU of a
// service into one bucket.
const skuLabel = (r) => r.skuName || r.skuId || '—';
const groupOf = (r) => `${r.service} · ${skuLabel(r)}`;

const add = (matrix, date, key, value) => {
  matrix[date] = matrix[date] || {};
  matrix[date][key] = (matrix[date][key] || 0) + (value || 0);
};

// buildPivot turns usage rows into the two column models and returns the dates
// plus a viewFor(metric) accessor. Every view exposes the same shape, so the
// table markup and the CSV writer never branch on the metric themselves.
export function buildPivot(rows = [], { fallbackCurrency = 'USD' } = {}) {
  const dates = [...new Set(rows.map((r) => r.date).filter(Boolean))].sort();

  // A money column only carries its currency in the header when the same SKU
  // arrives in more than one — otherwise every header would be suffixed.
  const currenciesOf = {};
  for (const r of rows) {
    const g = groupOf(r);
    (currenciesOf[g] = currenciesOf[g] || new Set()).add(r.currency || fallbackCurrency);
  }

  const moneyMeta = {};
  const usageMeta = {};
  const moneyWeight = {};
  const usageWeight = {};
  const cells = { cost: {}, billing: {}, usage: {} };

  for (const r of rows) {
    if (!r.date) continue;
    const group = groupOf(r);
    const currency = r.currency || fallbackCurrency;
    const unit = r.unit || '';
    // Ordering weight: what the line is worth, so the biggest spend leads in
    // both tables and switching metric never reshuffles the columns under you.
    // Billing stands in for cost on the client page, where the buy side is
    // stripped before the rows are sent.
    const weight = Math.abs(r.cost || r.billing || 0);

    const moneyKey = `${group}||${currency}`;
    if (!moneyMeta[moneyKey]) {
      const split = (currenciesOf[group]?.size || 1) > 1;
      moneyMeta[moneyKey] = {
        currency,
        label: split ? `${group} (${currency})` : group,
      };
    }
    add(cells.cost, r.date, moneyKey, r.cost);
    add(cells.billing, r.date, moneyKey, r.billing);
    moneyWeight[moneyKey] = (moneyWeight[moneyKey] || 0) + weight;

    const usageKey = `${group}||${unit}`;
    if (!usageMeta[usageKey]) {
      usageMeta[usageKey] = { unit, label: unit ? `${group} · ${unit}` : group };
    }
    add(cells.usage, r.date, usageKey, r.quantity);
    usageWeight[usageKey] = (usageWeight[usageKey] || 0) + weight;
  }

  const byWeight = (w) => (a, b) => w[b] - w[a];
  const moneyCols = Object.keys(moneyWeight).sort(byWeight(moneyWeight));
  const usageCols = Object.keys(usageWeight).sort(byWeight(usageWeight));

  // Which money columns belong to which currency: each currency present gets
  // its own row/grand Total column (just "Total" when there is only one).
  const colsByCurrency = {};
  for (const k of moneyCols) {
    const c = moneyMeta[k].currency;
    (colsByCurrency[c] = colsByCurrency[c] || []).push(k);
  }
  const currencies = Object.keys(colsByCurrency).sort();

  const columnTotal = (matrix, k) =>
    dates.reduce((s, d) => s + (matrix[d]?.[k] || 0), 0);

  const moneyView = (metric) => {
    const matrix = cells[metric];
    const rowTotal = (d, c) =>
      (colsByCurrency[c] || []).reduce((s, k) => s + (matrix[d]?.[k] || 0), 0);
    return {
      metric,
      money: true,
      cols: moneyCols,
      label: (k) => moneyMeta[k].label,
      value: (d, k) => matrix[d]?.[k] || 0,
      text: (v, k) => currencyExact(v, moneyMeta[k].currency),
      // Money exports at exactly 2 decimals, matching the amounts on screen.
      num: (v) => v.toFixed(2),
      // Row totals are money-only: one date of usage mixes GB months, OCPU
      // hours, and request counts, so there is nothing to add across the row.
      rowTotals: true,
      totalKeys: currencies,
      totalLabel: (c) => (currencies.length <= 1 ? 'Total' : `Total (${c})`),
      totalText: (v, c) => currencyExact(v, c),
      rowTotal,
      columnTotal: (k) => columnTotal(matrix, k),
      grandTotal: (c) => dates.reduce((s, d) => s + rowTotal(d, c), 0),
    };
  };

  const usageView = () => {
    const matrix = cells.usage;
    return {
      metric: 'usage',
      money: false,
      cols: usageCols,
      label: (k) => usageMeta[k].label,
      value: (d, k) => matrix[d]?.[k] || 0,
      text: (v) => quantity(v),
      // The reported precision, rounded to 6 places first so summing floats
      // cannot leak 100.83333299999999 into the file; Number() then drops any
      // trailing zeros.
      num: (v) => String(Number(v.toFixed(6))),
      rowTotals: false,
      totalKeys: [],
      totalLabel: () => 'Total',
      totalText: (v) => quantity(v),
      rowTotal: () => 0,
      columnTotal: (k) => columnTotal(matrix, k),
      grandTotal: () => 0,
    };
  };

  return {
    dates,
    currencies,
    viewFor: (metric) => (isMoneyMetric(metric) ? moneyView(metric) : usageView()),
  };
}

// Fields are separated by ';'. Decimals keep the '.' that OCI's usage API sends
// (its JSON numbers are always dot-decimal — see usageapi.UsageSummary), so the
// file reproduces the source figures rather than re-formatting them for a
// locale.
const CSV_SEP = ';';
const csvEsc = (v) => {
  const s = String(v ?? '');
  return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// pivotCsv writes whichever table the view describes, so the file matches what
// the tab on screen would show.
export function pivotCsv(view, dates) {
  const cell = (v) => (v ? view.num(v) : '');

  const out = [['Date', ...view.cols.map(view.label)]];
  if (view.rowTotals) out[0].push(...view.totalKeys.map(view.totalLabel));

  for (const d of dates) {
    const row = [d, ...view.cols.map((k) => cell(view.value(d, k)))];
    if (view.rowTotals) {
      row.push(...view.totalKeys.map((c) => view.num(view.rowTotal(d, c))));
    }
    out.push(row);
  }

  const footer = ['Total', ...view.cols.map((k) => view.num(view.columnTotal(k)))];
  if (view.rowTotals) {
    footer.push(...view.totalKeys.map((c) => view.num(view.grandTotal(c))));
  }
  out.push(footer);

  // Leading BOM so Excel detects UTF-8 and renders the '·' separators.
  return '﻿' + out.map((r) => r.map(csvEsc).join(CSV_SEP)).join('\r\n');
}

// downloadCsv saves the text as a file named by the caller.
export function downloadCsv(text, filename) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

// lineItemsCsv writes one line per stored usage row with the unit billing price
// beside the quantity and the amount billed — the price a client is charged per
// unit, which the pivot tables (amounts only) do not carry. Cost and the
// directory rate never appear: the API strips them for clients.
export function lineItemsCsv(rows = []) {
  const out = [[
    'Date', 'Tenant', 'Service', 'SKU', 'SKU name', 'Unit',
    'Quantity', 'Billing price', 'Billing', 'Currency',
  ]];
  for (const r of rows) {
    out.push([
      r.date, r.tenantName, r.service, r.skuId, r.skuName, r.unit,
      r.quantity ?? 0, r.billingPrice ?? '', r.billing ?? 0, r.currency,
    ]);
  }
  return '﻿' + out.map((r) => r.map(csvEsc).join(CSV_SEP)).join('\r\n');
}
